import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { Compare } from "@/components/guides/Compare";
import { TryTool } from "@/components/guides/TryTool";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["upc-vs-ean"];

const TOC = [
  { id: "the-short-answer", label: "The short answer" },
  { id: "history", label: "How we ended up with two" },
  { id: "telling-them-apart", label: "Telling them apart" },
  { id: "data-model", label: "Storing them without pain" },
  { id: "which-to-print", label: "Which to print today" },
  { id: "common-mistakes", label: "Common mistakes" },
  { id: "quick-decision", label: "Quick decision guide" },
  { id: "short-forms", label: "UPC-E and EAN-8" },
  { id: "upc-ean-meaning", label: "What Do UPC and EAN Mean?" },
  { id: "interchangeable", label: "Are UPC and EAN Interchangeable?" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "Can I sell on Amazon US with an EAN-13 instead of a UPC?",
    a: "Yes — Amazon accepts EAN-13 as a valid GTIN for US listings. The catalogue stores it as a GTIN-13 internally; the GS1 prefix doesn't have to be GS1 US. What matters is that the GTIN is registered to your brand at GS1 (Amazon increasingly enforces this) and that the check digit validates.",
  },
  {
    q: "Do I Need to Re-Issue My UPC Barcode for a European Launch?",
    a: "No. The UPC-A you already have is a valid GTIN-12 and reads as an EAN-13 across European POS once you pad it with a leading zero in the master data (12 → 13 digits). Some retailers re-print the artwork as an EAN-13 for shelf consistency, but the identifier itself doesn't change.",
  },
  {
    q: "Will an Older 12-Digit-Only POS Scanner Read an EAN-13?",
    a: "Most modern scanners do. Genuinely 1980s-era 12-digit-only scanners exist in pockets of US retail and may refuse the extra digit, but they're rare in 2026. If you're targeting one of those channels, ship UPC-A on the artwork even if your GS1 office issues 13-digit prefixes — the till still gets the GTIN.",
  },
  {
    q: "What's the Cheapest Way to Get a UPC for a Single Product?",
    a: "Apply to your national GS1 office for the smallest company prefix tier (in 2026, GS1 US offers a single-GTIN allocation under their GS1 US Data Hub). Resellers offer 'cheap UPCs' minted from defunct prefixes, but major retailers — including Amazon — verify provenance against GS1 and will reject those. The cheap option is the expensive option.",
  },
  {
    q: "Why Do Some EAN-13s Start With 977 or 978?",
    a: "Those prefixes aren't country codes — they're reserved by GS1 for specific publication types. 977 identifies an ISSN (serials like magazines), 978 and 979 identify ISBNs (books). The 13-digit barcode on the back of every book is an EAN-13 with a 978 / 979 prefix, with the publisher and check digit slotted in afterwards.",
  },
  {
    q: "What Is the Difference Between GTIN, UPC and EAN?",
    a: "GTIN is the number; UPC and EAN are the barcodes that carry it, and loosely the numbers printed under them. A UPC-A carries a GTIN-12 and an EAN-13 carries a GTIN-13, while the 14-digit GTIN-14 for cartons goes into an ITF-14 or GS1-128 barcode instead. All of them end in the same GS1 mod-10 check digit, so one validator can test any of them.",
  },
];

export const Route = createFileRoute("/barcode-guides/upc-vs-ean")({
  component: UpcVsEanGuide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function UpcVsEanGuide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/what-is-a-gtin",
          label: "What is a GTIN?",
          desc: "Same identifier, four lengths — and the check-digit maths that ties them together.",
        },
        {
          to: "/barcode-guides/barcode-formats",
          label: "Barcode formats field guide",
          desc: "Where UPC and EAN fit alongside Code 128, ITF-14, QR and Data Matrix.",
        },
        {
          to: "/upc-lookup",
          label: "UPC lookup",
          desc: "Validate a UPC-A or UPC-E and decode its number system.",
        },
        {
          to: "/ean-lookup",
          label: "EAN lookup",
          desc: "Validate an EAN-13 or EAN-8 and read its GS1 prefix.",
        },
      ]}
    >
      <p>
        You import a fifty-row product feed from a US distributor into a UK retailer&apos;s
        catalogue and half the joins fail. The values look numeric. The lengths look fine. But the
        EAN field has 12-digit strings where the system expected 13. Somewhere upstream, a leading
        zero went missing — and now the same product is two SKUs across two countries.
      </p>
      <p>
        That is the canonical <strong>UPC vs EAN</strong> problem — more precisely, <strong>UPC-A vs EAN-13</strong>, also written <strong>GTIN-12 vs GTIN-13</strong>. The
        difference between UPC and EAN sounds dramatic but the symbols are sibling formats from the
        same standards body, encoding the same kind of identifier. Modern scanners read both without
        thinking. The differences that catch you out are about data — column widths, GS1 prefixes,
        ownership of the allocation — not the symbols themselves. This guide explains where each one
        comes from, why your UPC suddenly grew a leading zero, and how to keep them straight in
        product data.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          UPC-A is 12 digits. EAN-13 is 13 digits. They&apos;re the same barcode underneath,
          allocated by different national offices of the same global standards body (GS1). Any UPC-A
          is also a valid EAN-13 if you stick a leading zero on the front. Every modern
          point-of-sale scanner accepts both.
        </p>

        <Compare
          panes={[
            {
              label: "UPC-A",
              caption: "12 digits · US & Canadian retail · GS1 US allocations",
              children: (
                <BarcodeFigure
                  format="upca"
                  value="036000291452"
                  formatLabel="UPC-A"
                  caption={
                    <>
                      Example UPC-A barcode encoding the 12-digit GTIN-12 <code>036000291452</code>.
                    </>
                  }
                />
              ),
            },
            {
              label: "EAN-13",
              caption: "13 digits · Worldwide · Any GS1 office",
              children: (
                <BarcodeFigure
                  format="ean13"
                  value="5012345678900"
                  formatLabel="EAN-13"
                  caption={
                    <>
                      Example EAN-13 barcode encoding the 13-digit GTIN-13 <code>5012345678900</code>.
                    </>
                  }
                />
              ),
            },
          ]}
        />

        <p>
          Drop a UPC-A into an EAN-13 field by adding a leading <code>0</code> and the value is
          still the same product. Some retailer systems do this automatically; some don&apos;t. When
          two ends of a pipeline disagree about whether to pad, you get the kind of phantom
          duplicate that opens this article.
        </p>

        <h3>UPC-A vs EAN-13 at a glance</h3>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Side-by-side comparison of UPC-A and EAN-13 barcodes: digits, region, GTIN type, POS
              scanner support, the commonest data slip-up, and when to use each.
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  Aspect
                </th>
                <th scope="col">
                  UPC-A
                </th>
                <th scope="col">
                  EAN-13
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">
                  Digits
                </th>
                <td>12</td>
                <td>13</td>
              </tr>
              <tr>
                <th scope="row">
                  Main region
                </th>
                <td>United States &amp; Canada</td>
                <td>
                  Worldwide (default outside North America)
                </td>
              </tr>
              <tr>
                <th scope="row">
                  GTIN type
                </th>
                <td>
                  GTIN-12 (stored as <code>0</code> + 12 digits in a GTIN-13 column)
                </td>
                <td>GTIN-13</td>
              </tr>
              <tr>
                <th scope="row">
                  POS scanner support
                </th>
                <td>Universal</td>
                <td>Universal</td>
              </tr>
              <tr>
                <th scope="row">
                  Most common slip-up
                </th>
                <td>
                  Leading zero stripped when stored as a number
                </td>
                <td>
                  Leading digit misread as country of manufacture
                </td>
              </tr>
              <tr>
                <th scope="row">
                  When to use
                </th>
                <td>
                  Your GS1 prefix was issued by GS1 US and your largest retail partner is in North
                  America
                </td>
                <td>
                  Any other case, or whenever you sell into more than one region
                </td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      </GuideArticleSection>

      <TableScroll>
        <table>
          <caption className="sr-only">
            Factual reference: digit count, region of origin, GS1 issuing office, leading-digit
            meaning and POS compatibility for UPC-A and EAN-13.
          </caption>
          <thead>
            <tr>
              <th scope="col">
                Aspect
              </th>
              <th scope="col">
                UPC-A
              </th>
              <th scope="col">
                EAN-13
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">
                Digit count
              </th>
              <td>12</td>
              <td>13</td>
            </tr>
            <tr>
              <th scope="row">
                Region of origin
              </th>
              <td>US / Canada (1973)</td>
              <td>International (1977)</td>
            </tr>
            <tr>
              <th scope="row">
                GS1 office
              </th>
              <td>GS1 US only</td>
              <td>Any GS1 office worldwide</td>
            </tr>
            <tr>
              <th scope="row">
                Leading-digit meaning
              </th>
              <td>Number-system digit</td>
              <td>
                First two or three are the GS1 prefix
              </td>
            </tr>
            <tr>
              <th scope="row">
                Retail POS compatibility
              </th>
              <td>Universal on modern hardware</td>
              <td>Universal on modern hardware</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>

      <GuideArticleSection id="history" title="How We Ended up With Two">
        <p>
          UPC-A came first, in 1973, designed for US supermarket point-of-sale by what is now GS1
          US. It worked, retail loved it, and within a few years Europe wanted in. The European
          response in 1977 was EAN-13: a 13-digit superset that kept every existing UPC-A intact
          (prefix it with <code>0</code> and you have an EAN-13) while opening up enough extra
          address space for the rest of the world&apos;s product registries.
        </p>
        <p>
          That backwards-compatibility decision is why the two formats are still around together
          half a century later. Nobody had to reissue a barcode. North American brands kept printing
          12 digits because their existing artwork already worked. Brands in Europe, Asia, Latin
          America and Oceania got their allocations from local GS1 offices, which handed out
          13-digit prefixes. The labels look different at the till but they live in the same
          catalogue.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="telling-them-apart" title="Telling Them Apart">
        <p>
          The simplest distinguisher is the digit count printed under the bars. Twelve = UPC-A.
          Thirteen = EAN-13. The leading digit of an EAN-13 sits slightly to the left of the bar
          pattern; the trailing digit of either format is the check digit that ties the value
          together.
        </p>
        <p>
          That leading digit on an EAN-13 is the <strong>GS1 prefix</strong>, and it&apos;s a common
          source of myth. A leading 5 doesn&apos;t mean &quot;made in the UK&quot; — it means the
          product&apos;s GS1 prefix was allocated by GS1 UK. The actual factory could be anywhere.
          The same brand can have prefixes from multiple GS1 offices for products launched in
          different markets.
        </p>

        <Callout variant="spec" title="GS1 prefix, not country">
          <p>
            UPC-A always has an implicit <code>0</code> prefix when expressed as a GTIN-13;
            that&apos;s why it&apos;s often described as having &quot;no prefix&quot;. EAN-13
            leading digits 30–37 are France; 50 is the UK; 49 is Japan; 690–699 is China. None of
            this tells you where the product is manufactured — only which GS1 office issued the
            company prefix.
          </p>
        </Callout>

        <p>
          If you want to spot-check a value without grabbing a barcode reader, the <Link to="/gtin-validator">GTIN Validator</Link> will tell you the length class and
          confirm the check digit in one step. For format-specific feedback — including the GS1
          prefix range — try the dedicated <Link to="/upc-lookup">UPC Lookup</Link> for 12-digit
          values and the <Link to="/ean-lookup">EAN Lookup</Link> for 13-digit ones.
        </p>

        <TryTool
          to="/gtin-validator"
          label="GTIN Validator"
          description="Confirm a UPC-A or EAN-13 has the correct GS1 mod-10 check digit."
          value="5012345678900"
        />
      </GuideArticleSection>

      <GuideArticleSection id="data-model" title="Storing Them Without Pain">
        <p>
          The pipeline problem at the top of this article almost always traces back to how UPC-A and
          EAN-13 are stored. Three rules of thumb keep the joins clean:
        </p>
        <ol>
          <li>
            <strong>Normalise on the way in.</strong> Pick GTIN-13 or GTIN-14 as the canonical
            length and pad on import. A 12-digit UPC-A is the GTIN-13 <code>0XXXXXXXXXXXX</code> or
            the GTIN-14 <code>00XXXXXXXXXXXX</code>; nothing changes about the meaning, only the
            column width.
          </li>
          <li>
            <strong>Validate the check digit at the boundary.</strong> Anything coming from a
            supplier feed, OCR or a free-text field gets run through the GS1 mod-10 check before
            it&apos;s trusted. That catches transposed digits, stripped zeros and well-meaning
            spreadsheet auto-formatting in one pass.
          </li>
          <li>
            <strong>Render the customer-facing form on the way out.</strong> The label printer knows
            whether to draw 12-digit UPC-A bars or 13-digit EAN-13 bars from the same stored value.
            The database doesn&apos;t need to mirror that choice.
          </li>
        </ol>

        <Callout variant="heads-up">
          <p>
            The single most common failure mode is a spreadsheet treating a UPC-A as a number and
            silently dropping the leading <code>0</code> when the column gets stored as GTIN-13.
            Either widen the column or store the value as a string from the start.
          </p>
        </Callout>

        <p>
          If you suspect a feed has been mangled, the Check Digit Calculator will tell you what the
          trailing digit <em>should</em> be for a given body — useful for spot-checking whether the
          issue is a stripped prefix or a corrupted body:
        </p>

        <TryTool
          to="/check-digit-calculator"
          label="Check Digit Calculator"
          description="Compute the correct GS1 mod-10 check digit for any UPC, EAN or GTIN body."
          value="501234567890"
          cta="Compute for 501234567890"
        />
      </GuideArticleSection>

      <GuideArticleSection id="which-to-print" title="Which to Print Today">
        <p>
          The choice between UPC-A and EAN-13 on a new package usually isn&apos;t yours to make in
          isolation. Three things constrain it:
        </p>
        <ul>
          <li>
            <strong>Your GS1 allocation.</strong> Whichever GS1 office issued your company prefix
            determines the length. A US allocation will yield UPC-A by default; an allocation from
            any other GS1 office will yield EAN-13.
          </li>
          <li>
            <strong>Your largest retail partner.</strong> Some retailers still require UPC-A in
            North American supply chains and EAN-13 elsewhere, even though their scanners read both.
            Check the data-spec from the partner with the biggest shelf space.
          </li>
          <li>
            <strong>Your packaging real estate.</strong> UPC-A and EAN-13 symbols are the same
            width (95 modules of bars and guards), so switching between them saves no room on small
            SKUs. For genuinely tiny packs there are the zero-suppressed short forms below.
          </li>
        </ul>
        <p>
          For global brands the simplest rule is: print EAN-13 worldwide. Every POS scanner reads
          it, every catalogue stores 13 digits without padding gymnastics, and you stop having to
          translate between formats. If you&apos;re a US-only brand with an existing UPC-A
          allocation, there&apos;s no reason to switch — the saving is purely cosmetic.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="common-mistakes" title="Common Mistakes">
        <p>
          Most UPC-vs-EAN bugs come from a small set of repeat offenders. If a lookup is failing or
          two SKUs that should be one keep diverging, work through these before going further:
        </p>
        <ul>
          <li>
            <strong>Stripping the leading zero.</strong> A UPC-A stored in a GTIN-13 column relies
            on the leading <code>0</code>; once a spreadsheet or implicit number cast drops it, the
            value becomes a different identifier.
          </li>
          <li>
            <strong>Treating UPC-A and EAN-13 as two products.</strong> <code>036000291452</code> and <code>0036000291452</code> are the same product. A duplicate row in your catalogue
            almost always means the pipeline is normalising inconsistently rather than that the
            supplier shipped two SKUs.
          </li>
          <li>
            <strong>Assuming the EAN prefix is the country of manufacture.</strong> The leading
            digits identify the GS1 office that issued the prefix, not where the item was made. A 50
            prefix means the company is registered with GS1 UK; it says nothing about the factory.
          </li>
          <li>
            <strong>Storing identifiers as numbers instead of strings.</strong> Numeric storage
            silently drops leading zeros, applies thousands separators in CSV exports and breaks
            joins. Treat every GTIN as text from the first time it touches your system.
          </li>
          <li>
            <strong>Printing the wrong format for the packaging.</strong> A UPC-A allocation
            can&apos;t be reprinted as an EAN-13 by &quot;just adding a zero&quot; on the artwork —
            the bars need regenerating from the padded GTIN. Validate the rendered symbol before
            signing off the print proof.
          </li>
        </ul>
        <p>
          If you suspect a whole feed has been mangled, run it through the <Link to="/batch-barcode-scanner">Batch Barcode Tools</Link> page — it flags every row
          that fails the GS1 mod-10 check and gives you a clean CSV back.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="quick-decision" title="Quick Decision Guide">
        <p>When you only need the practical answer, the rules collapse to four short ones:</p>
        <ul>
          <li>
            <strong>Print UPC-A</strong> on US or Canadian retail packaging when GS1 US has issued
            you a GTIN-12 company prefix and your largest retail partner is in North America.
          </li>
          <li>
            <strong>Print EAN-13</strong> on global retail packaging when GS1 has issued you a
            GTIN-13 prefix, or whenever you sell into more than one region — every POS scanner reads
            it.
          </li>
          <li>
            <strong>Store identifiers as GTIN strings internally</strong> — left-padded to GTIN-13
            or GTIN-14, always as text — so the same product is the same row across UPC-A and EAN-13
            markets.
          </li>
          <li>
            <strong>
              Validate with the <Link to="/gtin-validator">GTIN Validator</Link>
            </strong> before sending values to print or to a retailer feed. One round of validation at the
            boundary saves a week of master-data clean-up later.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="short-forms" title="UPC-E and EAN-8: the short forms">
        <p>
          For packages where 12–13 digits won&apos;t physically fit — small cosmetics, individual
          confectionery, electronic component packs — GS1 defined two short forms. They aren&apos;t
          just &quot;truncated&quot; UPC-A or EAN-13: they need their own GS1 allocations, and their
          check digit uses the same GS1 mod-10 method (a UPC-E carries the check digit of the UPC-A it expands to).
        </p>
        <Compare
          panes={[
            {
              label: "UPC-E",
              caption: "6-digit zero-suppressed form of UPC-A · expands at scan time",
              children: (
                <BarcodeFigure
                  format="upca"
                  value="042100005264"
                  formatLabel="UPC-A (compresses to UPC-E)"
                  caption={
                    <>
                      Source UPC-A <code>042100005264</code>; rendered as a compact UPC-E on tiny
                      packs and reconstituted to the full 12-digit GTIN at scan time.
                    </>
                  }
                />
              ),
            },
            {
              label: "EAN-8",
              caption: "8-digit independent allocation · separate GS1 numbering",
              children: (
                <BarcodeFigure
                  format="ean8"
                  value="96385074"
                  formatLabel="EAN-8"
                  caption={
                    <>
                      Example EAN-8 encoding the standalone 8-digit GTIN <code>96385074</code> — not
                      derivable from any EAN-13.
                    </>
                  }
                />
              ),
            },
          ]}
        />
        <p>
          UPC-E is essentially a printed-shorthand for a UPC-A that has trailing or middle zeros — a
          scanner reads the 6 visible digits and reconstitutes the 12-digit UPC-A internally. EAN-8
          is a different beast: a full standalone 8-digit GTIN allocated by GS1 to fit on items
          where EAN-13 simply doesn&apos;t fit. The two are not interchangeable and a brand
          can&apos;t generate one from the other without going back to GS1.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="upc-ean-meaning" title="What Do UPC and EAN Mean?">
        <p>
          UPC stands for Universal Product Code and EAN for European Article Number, the original
          name of the 13-digit system. Both names now refer to barcodes that carry a GS1 Global Trade
          Item Number (GTIN).
        </p>
        <p>
          The Universal Product Code was the North American grocery industry&apos;s numbering system
          for checkout, and UPC-A is its full-size 12-digit symbol. The European Article Numbering
          association extended it to 13 digits so that countries outside North America could get
          number ranges of their own. When EAN International adopted the GS1 name in 2005, EAN
          survived as an abbreviation that GS1 now expands as International Article Number.
        </p>
        <p>
          On a retailer or marketplace form, a field labeled &quot;UPC/EAN&quot; or &quot;UPC or
          EAN&quot; asks for one thing: the product&apos;s GTIN. Each term points to a slightly
          different part of the system:
        </p>
        <ul>
          <li>
            <strong>UPC</strong> usually means UPC-A, the barcode for a 12-digit GTIN-12 used in the
            United States and Canada. UPC-E is its zero-suppressed short form for small packs.
          </li>
          <li>
            <strong>EAN</strong> usually means EAN-13, the barcode for a 13-digit GTIN-13 used in
            most other countries. EAN-8 is the separate 8-digit short form.
          </li>
          <li>
            <strong>GTIN</strong> is the GS1 name for the number itself, in any of its four lengths
            (8, 12, 13 or 14 digits), whatever barcode carries it.
          </li>
        </ul>
        <p>
          To see what a particular code tells you, the <Link to="/upc-lookup">UPC Lookup</Link> validates a UPC-A, expands a UPC-E and names the number-system digit, and the <Link to="/ean-lookup">EAN Lookup</Link> checks an EAN-13 or EAN-8 and reports which GS1 prefix range it falls in.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="interchangeable" title="Are UPC and EAN Interchangeable?">
        <p>
          As numbers, yes: a UPC-A and the EAN-13 formed by adding a leading zero identify the same
          product. In forms and fixed-length fields, they are interchangeable only when both sides
          add or remove that zero the same way.
        </p>
        <p>
          The symbols are close relatives. UPC-A and EAN-13 share the same bar patterns and the same
          95-module width, and an EAN-13 that starts with 0 prints exactly the same bars as the UPC-A
          without that zero. Under the GS1 US Sunrise 2005 initiative, retailers in the United States
          and Canada were asked to scan EAN-13 as well as UPC-A from January 1, 2005, so in the UPC
          vs EAN barcode question either symbol works at a modern checkout.
        </p>
        <p>The difference between UPC and EAN numbers starts to matter in three places:</p>
        <ul>
          <li>
            <strong>Fixed-length fields.</strong> A form that accepts exactly 12 digits rejects an
            EAN-13, and a 13-digit field may reject a UPC-A until you add the leading zero yourself.
          </li>
          <li>
            <strong>EAN-13s that start with another digit.</strong> Only an EAN-13 that begins with
            0 can be written as a UPC-A. A code starting with 4, 5 or any other non-zero digit has no
            12-digit form.
          </li>
          <li>
            <strong>The short forms.</strong> A UPC-E expands to a full UPC-A, but an EAN-8 is a
            GTIN-8 in its own right with no 12- or 13-digit equivalent.
          </li>
        </ul>
        <p>
          For UPC vs EAN-13 the conversion is mechanical. When a form asks for a UPC and you hold an EAN-13 that begins with 0, drop the zero; when
          it asks for an EAN-13 and you hold a UPC-A, add one. The check digit stays the same both
          ways, because a leading zero adds nothing to the weighted sum.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "UPC-A and EAN-13 are the same product identifier in different sleeves — a UPC-A is an EAN-13 with a leading zero.",
          "Normalise everything to GTIN-13 (or GTIN-14) at the data layer; render UPC-A or EAN-13 on labels from the stored value.",
          "Leading digits identify the GS1 office, not the country of manufacture.",
          "Modern POS scanners read both interchangeably — the choice between them is about data hygiene and retail partners, not about scanning.",
          "UPC-E and EAN-8 are separate short-form symbologies for tiny packaging; they need their own GS1 allocations.",
        ]}
      />


      <GuideArticleSection id="references" title="References">
        <p>The digit counts, prefixes and symbol rules in this guide come from these GS1 documents.</p>
        <ul>
          <li>
            GS1 General Specifications — the canonical document covering UPC-A, EAN-13, EAN-8 and
            the GS1 mod-10 check digit. Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a> and updated annually.
          </li>
          <li>
            GS1 EAN/UPC barcodes — overview of the two symbologies and their permitted applications: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
          <li>
            GTIN identifier reference — what a GTIN is, the four lengths and the rules for assigning
            new ones: <a
              href="https://www.gs1.org/standards/id-keys"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/id-keys
            </a>
            .
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        Got a feed with both formats mixed in? The <Link to="/batch-barcode-scanner">Batch Barcode Scanner</Link> page validates and normalises a
        list of values in one pass. For the wider picture of where UPC and EAN sit among other
        symbologies, see the <Link to="/barcode-guides/barcode-formats">barcode formats field guide</Link>.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
